// assets/AllCourses/Exercises/MultipleChoiceExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function MultipleChoiceExercise({
  exercise = {},
  onUpdate,           // Edit mode only
  errors = {},
  isPreview = false,  // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {}, // Student: report correct/incorrect
}) {
  const isEditMode = typeof onUpdate === "function";

  // Safe questions
  const questions = Array.isArray(exercise.multipleChoiceQuestions)
    ? exercise.multipleChoiceQuestions
    : [];

  // Student state
  const [state, setState] = useState({
    answers: {}, // key = qIndex, value = selected option string
    hasInteracted: false,
  });
  const completedRef = useRef(false);

  // Sync saved attempt (student mode)
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;
    setState({
      answers: typeof exercise.attempt.answers === "object" ? exercise.attempt.answers : {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
  }, [isPreview, exercise.attempt]);

  // Reset on exercise change
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise._id, isPreview]);

  // Normalize text for comparison
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  // === EDIT MODE FUNCTIONS ===
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("multipleChoiceQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const current = next[qIdx] || {
      question: "",
      options: [],
      answer: "",
      requireCapitalization: false,
    };
    next[qIdx] = { ...current, [field]: value };
    updateQuestions(next);
  };

  const handleOptionChange = (qIdx, optIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx] || { options: [] };
    const options = [...q.options];
    options[optIdx] = value;
    next[qIdx] = { ...q, options };
    updateQuestions(next);
  };

  const handleAddOption = (qIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx] || { options: [] };
    next[qIdx] = { ...q, options: [...q.options, ""] };
    updateQuestions(next);
  };

  const handleRemoveOption = (qIdx, optIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx] || { options: [] };
    next[qIdx] = { ...q, options: q.options.filter((_, i) => i !== optIdx) };
    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: Boolean(checked),
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      {
        question: "",
        options: [],
        answer: "",
        requireCapitalization: false,
      },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  // === STUDENT MODE FUNCTIONS ===
  const handleChange = (qIdx, value) => {
    if (isEditMode || showResult) return;

    setState(prev => {
      const next = {
        ...prev,
        answers: {
          ...prev.answers,
          [qIdx]: value,
        },
        hasInteracted: true,
      };

      setAttempt(next);

      const allAnswered = questions.every((_, i) => next.answers[i] !== undefined);
      if (allAnswered) {
        checkAnswers(next);
      }

      return next;
    });
  };

  const checkAnswers = (currentState) => {
    if (completedRef.current || !isPreview) return;
    completedRef.current = true;

    const allCorrect = questions.every((q, qIdx) => {
      const user = currentState.answers[qIdx] || "";
      return normalize(user, q.requireCapitalization) === normalize(q.answer, q.requireCapitalization);
    });

    onComplete(exercise._id, allCorrect);
  };

  // === EDIT MODE RENDER ===
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No questions yet. Click "Add Question" to begin.
          </p>
        )}

        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <TextareaField
              label={`Question ${qIdx + 1}`}
              value={q.question || ""}
              onChange={(v) => handleQuestionChange(qIdx, "question", v)}
              placeholder="What is the capital of France?"
              rows={3}
              required
              error={errors?.[`exercise-${exercise.index}-mc-question-${qIdx}`]}
            />

            {/* Options */}
            <div className="space-y-3">
              <Label>Options</Label>
              {q.options.map((opt, optIdx) => (
                <div key={optIdx} className="flex gap-3 items-end">
                  <StringField
                    value={opt}
                    onChange={(v) => handleOptionChange(qIdx, optIdx, v)}
                    placeholder="Option"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleRemoveOption(qIdx, optIdx)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => handleAddOption(qIdx)}>
                Add Option
              </Button>
            </div>

            <StringField
              label="Correct Answer"
              value={q.answer || ""}
              onChange={(v) => handleQuestionChange(qIdx, "answer", v)}
              placeholder="Paris"
              required
              error={errors?.[`exercise-${exercise.index}-mc-answer-${qIdx}`]}
            />

            <CapitalizationRule
              checked={!!q.requireCapitalization}
              onChange={(c) => handleCapitalizationChange(qIdx, c)}
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove Question
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  // === STUDENT PREVIEW MODE ===
  if (questions.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No multiple choice questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardDescription>
          Choose the correct answer for each question.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {questions.map((q, qIdx) => {
          const selected = state.answers[qIdx] || "";
          const isCorrect = selected && normalize(selected, q.requireCapitalization) === normalize(q.answer, q.requireCapitalization);

          return (
            <div key={qIdx} className="p-6 bg-muted rounded-lg space-y-4">
              <p className="text-base md:text-lg font-medium">
                {q.question}
              </p>

              <RadioGroup
                value={selected}
                onValueChange={(v) => handleChange(qIdx, v)}
                disabled={showResult}
              >
                <div className="space-y-3">
                  {q.options.map((opt, optIdx) => {
                    const correct = normalize(opt, q.requireCapitalization) === normalize(q.answer, q.requireCapitalization);
                    const isSelected = selected === opt;

                    return (
                      <div
                        key={optIdx}
                        className="flex items-center space-x-3 p-3 rounded-lg border bg-card"
                      >
                        <RadioGroupItem
                          value={opt}
                          id={`mc-${qIdx}-${optIdx}`}
                          className={cn(
                            showResult && isSelected && correct && "border-green-500",
                            showResult && isSelected && !correct && "border-red-500"
                          )}
                        />
                        <Label
                          htmlFor={`mc-${qIdx}-${optIdx}`}
                          className={cn(
                            "flex-1 cursor-pointer text-base",
                            showResult && isSelected && correct && "text-green-700 font-semibold",
                            showResult && isSelected && !correct && "text-red-700 font-semibold"
                          )}
                        >
                          {opt}
                          {showResult && isSelected && (
                            <span className="ml-4 text-sm">
                              {correct ? "Correct" : `Wrong → ${q.answer}`}
                            </span>
                          )}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </RadioGroup>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}